import React from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Package, Truck, Mail, FileWarning, Clock, TrendingUp,
  AlertTriangle, CheckCircle2, Users, ArrowRight,
} from 'lucide-react';

// ---------- small presentational pieces ----------
const KpiCard = ({ icon: Icon, label, value, tint = 'slate', sub, onClick, testid }) => {
  const colorMap = {
    slate: 'text-slate-600 bg-slate-50 border-slate-200',
    indigo: 'text-indigo-600 bg-indigo-50 border-indigo-200',
    emerald: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    amber: 'text-amber-600 bg-amber-50 border-amber-200',
    red: 'text-red-600 bg-red-50 border-red-200',
    blue: 'text-blue-600 bg-blue-50 border-blue-200',
  };
  return (
    <Card
      className={`border ${onClick ? 'cursor-pointer hover:shadow-sm transition-shadow' : ''}`}
      onClick={onClick}
      data-testid={testid}
    >
      <CardContent className="p-3.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">{label}</p>
            <p className="text-2xl font-semibold mt-0.5 tabular-nums">{value ?? '—'}</p>
            {sub && <p className="text-[10px] text-muted-foreground mt-1">{sub}</p>}
          </div>
          <div className={`rounded-md p-1.5 border ${colorMap[tint]}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PipelineBar = ({ manifests = {} }) => {
  const stages = [
    { key: 'draft', label: 'Draft', color: 'bg-slate-300' },
    { key: 'emailed', label: 'Emailed', color: 'bg-amber-400' },
    { key: 'challans_created', label: 'DC Created', color: 'bg-blue-400' },
    { key: 'dispatched_to_customer', label: 'Dispatched', color: 'bg-emerald-500' },
  ];
  const total = stages.reduce((s, st) => s + (manifests[st.key] || 0), 0) || 1;
  return (
    <div>
      <div className="flex h-2 rounded overflow-hidden bg-slate-100">
        {stages.map(st => (
          <div
            key={st.key}
            className={st.color}
            style={{ width: `${((manifests[st.key] || 0) / total) * 100}%` }}
            title={`${st.label}: ${manifests[st.key] || 0}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px]">
        {stages.map(st => (
          <div key={st.key} className="flex items-center gap-1.5">
            <span className={`inline-block w-2 h-2 rounded ${st.color}`} />
            <span className="text-muted-foreground">{st.label}</span>
            <span className="font-semibold tabular-nums">{manifests[st.key] || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SlaCard = ({ sla }) => {
  const avg = sla?.avg_hours_manifest_to_customer_email || 0;
  const median = sla?.median_hours_manifest_to_customer_email || 0;
  const sample = sla?.sample_size || 0;
  const fmt = (h) => h >= 48 ? `${(h / 24).toFixed(1)}d` : `${h.toFixed(1)}h`;
  return (
    <Card data-testid="sla-card">
      <CardHeader className="py-3 px-4 border-b">
        <CardTitle className="text-xs flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-indigo-500" /> Dispatch SLA</CardTitle>
        <CardDescription className="text-[11px]">Time from manifest creation → customer email</CardDescription>
      </CardHeader>
      <CardContent className="p-4">
        {sample === 0 ? (
          <p className="text-xs text-muted-foreground">No SLA data yet — dispatches will start showing here once you use One-Click Dispatch.</p>
        ) : (
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-2xl font-semibold tabular-nums">{fmt(avg)}</p>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Average</p>
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums">{fmt(median)}</p>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Median</p>
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums">{sample}</p>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Sample Size</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------- main component ----------
export default function DispatchDashboard({ stats, onGoToManifests, onGoToChallans }) {
  const s = stats || {};
  const byCustomer = s.by_customer || [];
  const recent = s.recent_dispatches || [];
  const missingInv = s.invoices_missing_list || [];
  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
  };

  return (
    <div className="space-y-4" data-testid="dispatch-dashboard">
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard icon={Truck} label="Total DCs" value={s.total_challans} tint="slate" testid="kpi-total-dcs" />
        <KpiCard icon={TrendingUp} label="Dispatched Today" value={s.dispatched_today} sub={`Week ${s.dispatched_this_week || 0} / Month ${s.dispatched_this_month || 0}`} tint="emerald" testid="kpi-today" />
        <KpiCard icon={Mail} label="Pending Customer Email" value={s.pending_customer_email} tint="amber" onClick={() => onGoToChallans?.({ email: 'not_sent' })} testid="kpi-pending-email" />
        <KpiCard icon={FileWarning} label="Missing Invoices" value={s.invoices_pending_after_dispatch} sub="Emailed but no invoice attached" tint="red" onClick={() => onGoToChallans?.({ reconciliation: 'pending' })} testid="kpi-missing-inv" />
        <KpiCard icon={Package} label="Pending Reconciliation" value={s.pending_reconciliation} tint="blue" onClick={() => onGoToChallans?.({ reconciliation: 'pending' })} testid="kpi-pending-reconcile" />
        <KpiCard icon={CheckCircle2} label="Manifests Dispatched" value={s.manifests?.dispatched_to_customer} tint="emerald" onClick={onGoToManifests} testid="kpi-manifests-dispatched" />
      </div>

      {/* Pipeline + SLA */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card data-testid="manifest-pipeline-card">
          <CardHeader className="py-3 px-4 border-b">
            <CardTitle className="text-xs flex items-center gap-1.5"><ArrowRight className="w-3.5 h-3.5 text-blue-500" /> Manifest Pipeline</CardTitle>
            <CardDescription className="text-[11px]">Distribution of manifests across stages</CardDescription>
          </CardHeader>
          <CardContent className="p-4">
            <PipelineBar manifests={s.manifests || {}} />
          </CardContent>
        </Card>
        <SlaCard sla={s.sla} />
      </div>

      {/* Missing invoices + By customer */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card data-testid="missing-invoices-card">
          <CardHeader className="py-3 px-4 border-b">
            <CardTitle className="text-xs flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-red-500" /> Invoices Missing After Dispatch</CardTitle>
            <CardDescription className="text-[11px]">DCs emailed to customer but no invoice attached</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {missingInv.length === 0 ? (
              <p className="p-4 text-xs text-muted-foreground">All clear — every dispatched DC has an invoice attached.</p>
            ) : (
              <div className="divide-y max-h-64 overflow-auto">
                {missingInv.map((inv, i) => (
                  <div key={inv.id || i} className="flex items-center justify-between px-3 py-2 text-xs hover:bg-slate-50">
                    <div className="min-w-0">
                      <p className="font-mono text-[11px] truncate">{inv.dc_number}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{inv.customer_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] text-muted-foreground">Emailed</p>
                      <p className="text-[11px] tabular-nums">{fmtDate(inv.customer_emailed_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="by-customer-card">
          <CardHeader className="py-3 px-4 border-b">
            <CardTitle className="text-xs flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-indigo-500" /> Top Customers — Delivery Challans</CardTitle>
            <CardDescription className="text-[11px]">Volume + outstanding items per customer</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {byCustomer.length === 0 ? (
              <p className="p-4 text-xs text-muted-foreground">No data yet.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Customer</th>
                    <th className="text-right px-3 py-2 font-medium">Total</th>
                    <th className="text-right px-3 py-2 font-medium">Dispatched</th>
                    <th className="text-right px-3 py-2 font-medium">Pending Email</th>
                    <th className="text-right px-3 py-2 font-medium">Unreconciled</th>
                  </tr>
                </thead>
                <tbody>
                  {byCustomer.slice(0, 10).map(row => (
                    <tr key={row.customer_id || row.customer_name} className="border-t hover:bg-slate-50/50">
                      <td className="px-3 py-1.5 truncate max-w-[220px]">{row.customer_name || '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{row.total}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{row.dispatched}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {row.pending_email > 0 ? <Badge variant="secondary" className="text-[10px]">{row.pending_email}</Badge> : 0}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {row.pending_reconcile > 0 ? <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">{row.pending_reconcile}</Badge> : 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Dispatches */}
      <Card data-testid="recent-dispatches-card">
        <CardHeader className="py-3 px-4 border-b flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-xs flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-slate-500" /> Recent Dispatch Challans</CardTitle>
            <CardDescription className="text-[11px]">Last 10 delivery challans created</CardDescription>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onGoToChallans?.({})}>
            View all <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {recent.length === 0 ? (
            <p className="p-4 text-xs text-muted-foreground">No dispatches yet.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">DC #</th>
                  <th className="text-left px-3 py-2 font-medium">Customer</th>
                  <th className="text-left px-3 py-2 font-medium">Category</th>
                  <th className="text-right px-3 py-2 font-medium">Items</th>
                  <th className="text-left px-3 py-2 font-medium">Emailed</th>
                  <th className="text-left px-3 py-2 font-medium">Reconciled</th>
                  <th className="text-right px-3 py-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {recent.map(c => (
                  <tr key={c.id} className="border-t hover:bg-slate-50/50">
                    <td className="px-3 py-1.5 font-mono text-[11px]">{c.dc_number}</td>
                    <td className="px-3 py-1.5 truncate max-w-[200px]">{c.customer_name || '—'}</td>
                    <td className="px-3 py-1.5">
                      <Badge variant="outline" className="text-[10px]">{c.challan_category || 'delivery'}</Badge>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{c.total_items ?? (c.items || []).length}</td>
                    <td className="px-3 py-1.5">
                      {c.customer_emailed_at
                        ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Yes</Badge>
                        : <span className="text-[11px] text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3 py-1.5">
                      {c.reconciliation_status === 'reconciled'
                        ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Yes</Badge>
                        : c.reconciliation_status === 'not_applicable'
                          ? <span className="text-[11px] text-muted-foreground">N/A</span>
                          : <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">Pending</Badge>}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmtDate(c.dispatch_date || c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
